'use client'

/**
 * El diploma en HTML, tal y como se ve en pantalla y sale por la impresora.
 *
 * Es HTML y no el canvas a propósito (ver `PrintableSheet`): se puede leer,
 * seleccionar y ampliar. El canvas solo entra en juego al guardar el PNG, y
 * `drawCertificate` reproduce esta misma maqueta.
 */

import { Fragment } from 'react'
import type { ToothCertificate } from '@/lib/guides/tools/tooth-certificate'
import { drawCertificate } from '@/lib/guides/tools/certificate-canvas'
import { PRODUCT_EVENTS } from '@/lib/product-analytics'
import { PrintableSheet } from './printable-sheet'

/** Dorado del marco y del sello: el color que hace que parezca oficial. */
const GOLD = 'border-[#a9752c] text-[#a9752c]'

function Seal({ lines }: { lines: string[] }) {
  return (
    <div
      aria-hidden
      className={`flex size-24 shrink-0 flex-col items-center justify-center rounded-full border-4 text-center text-[10px] font-bold uppercase leading-tight md:size-28 md:text-xs ${GOLD}`}
    >
      {lines.map((line) => (
        <span key={line}>{line}</span>
      ))}
    </div>
  )
}

function Details({ details }: { details: ToothCertificate['details'] }) {
  return (
    <dl className="mx-auto mt-7 grid max-w-md gap-x-6 gap-y-2 text-left text-sm sm:grid-cols-[auto_1fr] md:text-base">
      {details.map((detail) => (
        <Fragment key={detail.label}>
          <dt className="font-semibold uppercase tracking-wide text-[#3f3222]/60">
            {detail.label}
          </dt>
          {/* Tinta, no dorado: el dorado es del marco, y aquí lo heredaría. */}
          <dd className="font-serif text-[#3f3222]">{detail.value}</dd>
        </Fragment>
      ))}
    </dl>
  )
}

export function CertificateSheet({
  certificate,
  filename,
  analyticsProperties,
}: {
  certificate: ToothCertificate
  filename: string
  /** Contexto del evento de guardado. Nunca datos del niño. */
  analyticsProperties: Record<string, unknown>
}) {
  return (
    <PrintableSheet
      className="rounded-2xl bg-[#fdf8ec] p-3 text-[#3f3222] shadow-xl"
      draw={(canvas) => drawCertificate(canvas, certificate)}
      filename={filename}
      savedEvent={PRODUCT_EVENTS.GUIDE_CERTIFICATE_SAVED}
      analyticsProperties={analyticsProperties}
    >
      <div className={`border-4 p-1 ${GOLD}`}>
        <div className={`border px-5 py-8 text-center md:px-10 md:py-12 ${GOLD}`}>
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] md:text-xs">
            {certificate.authority}
          </p>
          <p className="mt-4 font-serif text-2xl font-bold text-[#3f3222] md:text-4xl">
            {certificate.title}
          </p>

          <p className="mt-7 font-serif text-base italic text-[#3f3222]/70">
            {certificate.awardedTo}
          </p>
          <p className="mt-1 break-words font-serif text-3xl font-bold md:text-5xl">
            {certificate.childName}
          </p>
          <p className="mx-auto mt-5 max-w-xl font-serif text-base leading-relaxed text-[#3f3222] md:text-lg">
            {certificate.citation}
          </p>

          <Details details={certificate.details} />

          <div className="mt-9 flex items-end justify-between gap-4">
            <Seal lines={certificate.seal} />
            <div className="min-w-0 text-right">
              <p className="font-serif text-2xl italic text-[#3f3222] md:text-3xl">
                {certificate.signature}
              </p>
              <hr className="mt-1 border-t border-[#3f3222]/40" />
              <p className="mt-1 text-[11px] uppercase tracking-wide md:text-xs">
                {certificate.signatureRole}
              </p>
            </div>
          </div>
        </div>
      </div>
    </PrintableSheet>
  )
}
